/**
 * The server's confirmation of the message a send just persisted: its real id,
 * and its canonical text — REDACTED when a session credential is bound, so the
 * optimistic bubble must take this text, not only the id, or the raw secret the
 * user typed stays on screen until a reload.
 *
 * Two carriers, one answer (#874):
 *  - `RUN_STARTED.metadata.papan.userMessage` — what the server sends when the
 *    client opts out of MESSAGES_SNAPSHOT (`forwardedProps.papanOmitSnapshot`).
 *    Names the exact message, including an attachment-only send with no text.
 *  - MESSAGES_SNAPSHOT's last user message — an older server, or one that did not
 *    honour the flag. Kept so a rolling deploy works in both directions.
 *
 * Pure, so it is unit-tested without React.
 */
export interface SentMessageConfirmation {
  id: string
  /** The persisted text; absent when the carrier did not include it. */
  content?: string
  /** The text was too large for the frame (#874): fetch this one message's saved text. */
  contentOmitted?: true
}

export function sentMessageConfirmation(aev: { type: string; [k: string]: unknown }): SentMessageConfirmation | null {
  if (aev.type === 'RUN_STARTED') {
    const um = (aev.metadata as { papan?: { userMessage?: { id?: unknown; content?: unknown; contentOmitted?: unknown } } } | undefined)?.papan?.userMessage
    if (!um || typeof um.id !== 'string' || !um.id) return null
    if (typeof um.content === 'string') return { id: um.id, content: um.content }
    return um.contentOmitted === true ? { id: um.id, contentOmitted: true } : { id: um.id }
  }
  if (aev.type === 'MESSAGES_SNAPSHOT') {
    const msgs = (aev.messages as Array<{ id?: unknown; role?: unknown; content?: unknown }> | undefined) ?? []
    const lastUser = [...msgs].reverse().find((m) => m.role === 'user')
    if (!lastUser || typeof lastUser.id !== 'string' || !lastUser.id) return null
    return typeof lastUser.content === 'string' ? { id: lastUser.id, content: lastUser.content } : { id: lastUser.id }
  }
  return null
}
